import { Box, Flex, Image, Text, Tooltip } from '@chakra-ui/react';
import { MTK_DECIMALS } from '@meteraprotocol/core';
import BigNumber from 'bignumber.js';
import React, { Dispatch, SetStateAction } from 'react';
import { NumericFormat } from 'react-number-format';
import { Balance } from '../blockchain/client';
import ProfileSvg from './atoms/svg/profile-svg';
import StatusDot from './atoms/svg/statusDot-svg';
import { themeVariables } from '../theme';
import { IAssetInPortfolio } from '../types';
import StepGuide from './steps-guide';

const cardanoSrc =
  'https://sqvzfgobtlzbovqbkaax.supabase.co/storage/v1/object/public/sdk-assets//cardanoimage.png';
const swapSrc =
  'https://sqvzfgobtlzbovqbkaax.supabase.co/storage/v1/object/public/sdk-assets//swap-arrow.svg';

type BuySellInputProps = {
  type: 'mint' | 'burn';
  portfolioTicker: string;
  balance: Balance | null;
  mtkUnit: string;
  setInputValue: Dispatch<SetStateAction<BigNumber>>;
  switchType: Dispatch<SetStateAction<'mint' | 'burn'>>;
  inputValue: BigNumber;
  portfolioImageUrl: string;
  expectedAmount: BigNumber;
  withdrawLovelace: BigNumber;
  showBreakdown: () => void;
  hasInsufficientToken: boolean;
  portfolioAssets: IAssetInPortfolio[];
  background?: string;
};

function BuySellInput({
  type,
  portfolioTicker,
  portfolioImageUrl,
  balance,
  inputValue,
  setInputValue,
  mtkUnit,
  expectedAmount,
  withdrawLovelace,
  switchType,
  showBreakdown,
  hasInsufficientToken,
  portfolioAssets,
  background,
}: BuySellInputProps) {
  const handleFocus = () => {};

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setInputValue(new BigNumber(0)); // Reset to 0 if empty
    }
  };

  const handleSetMaxInput = () => {
    if (!balance) return;
    setInputValue(
      BigNumber(balance[mtkUnit]?.toString() || 0).div(10 ** MTK_DECIMALS),
    );
  };

  const PURPLE_STYLE = {
    base: {
      background: themeVariables.cards.background,
      animation: `${themeVariables.animatedBorder.bgSpin} 3s linear infinite`,
    },
    hover: {
      background: `conic-gradient(
      from var(--animated-border),
      #171717a3,
      #171717a3 5%,
      #171717a3 60%,
      #171717a3 95%
    ) padding-box,
    conic-gradient(
      from var(--animated-border),
      transparent 0%,
      transparent 45%, /* Make it fade until 45% */
      #e8a92d, /* Base color */
      #fcd34d 85%, /* Lighter shade of base color */
      transparent 100% /* Fade to transparent after the 55% mark */
    ) border-box`,
      animation: 'bg-spin 3s linear infinite',
    },
  };

  const YELLOW_STYLE = {
    base: {
      background: `conic-gradient(
      from var(--animated-border),
      #171717a3,
      #171717a3 5%,
      #171717a3 60%,
      #171717a3 95%
    ) padding-box,
    conic-gradient(
      from var(--animated-border),
      transparent 0%,
      transparent 45%, /* Make it fade until 45% */
      #e8a92d, /* Base color */
      #fcd34d 85%, /* Lighter shade of base color */
      transparent 100% /* Fade to transparent after the 55% mark */
    ) border-box`,
      animation: 'bg-spin 3s linear infinite',
    },
    hover: {
      background: `conic-gradient(
      from var(--animated-border),
      #171717a3,
      #171717a3 5%,
      #171717a3 60%,
      #171717a3 95%
    ) padding-box,
    conic-gradient(
      from var(--animated-border),
      transparent 0%,
      transparent 45%, /* Make it fade until 45% */
      #6d28d9,
      #a78bfa 85%, /* Adjust color stops for top-right and bottom-left corners */
      transparent 100%
    ) border-box`,
      animation: 'bg-spin 3s linear infinite',
    },
  };

  const currentStyle = hasInsufficientToken ? YELLOW_STYLE : PURPLE_STYLE;

  const renderSendInputBox = () => (
    <Flex
      border={`1px solid ${themeVariables.cards.stroke}`}
      _hover={{ border: `1px solid ${themeVariables.purple.metera}` }}
      _focusWithin={{ border: `1px solid ${themeVariables.purple.metera}` }}
      height="105px"
      borderRadius="1rem"
      padding="1.5rem"
      width="full"
      align="center"
    >
      <Flex
        justifyContent={'space-between'}
        alignItems={'center'}
        width={'100%'}
        gap={'8px'}
      >
        <Flex
          position={'relative'}
          width={'full'}
          flex={1}
          alignItems={'flex-start'}
        >
          <Flex flexDir="column" gap={1}>
            <Box fontSize="0.875rem" color="#A3A3A3">
              Send
            </Box>
            <NumericFormat
              className="mint-input"
              value={inputValue.gt(0) ? inputValue.toString() : ''}
              placeholder={
                type === 'mint' ? (hasInsufficientToken ? '0.0' : 'ADA') : '0.0'
              }
              valueIsNumericString
              onFocus={handleFocus}
              onBlur={handleBlur}
              thousandSeparator
              isAllowed={({ floatValue }) => (floatValue || 0) <= 10_000_000}
              max={10_000_000}
              decimalScale={MTK_DECIMALS}
              style={{
                width: '100%',
                color: themeVariables.offWhite.offWhite400,
                fontSize: '32px',
                fontWeight: '500',
                lineHeight: '32px',
                letterSpacing: '-0.02em',
                textAlign: 'left',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: 0,
                outline: 'none',
              }}
              onValueChange={(values) => {
                setInputValue(
                  new BigNumber(Number(values.floatValue ? values.value : 0)),
                );
              }}
            />
          </Flex>
        </Flex>
        <Flex flexDirection={'column'} color={'#A3A3A3'}>
          <Flex justify="right" cursor="pointer">
            {type === 'burn' && (
              <Flex
                justify="right"
                fontSize="0.75rem"
                color={themeVariables.neutral[400]}
                fontWeight={400}
                cursor={'pointer'}
                onClick={handleSetMaxInput}
                display={'flex'}
                flexDirection={'row'}
                gap={2}
                alignItems={'flex-end'}
              >
                <ProfileSvg color="#A3A3A3" />
                <span>
                  {BigNumber(balance?.[mtkUnit]?.toString() || 0)
                    .div(10 ** MTK_DECIMALS)
                    .toFixed(2)}
                </span>
              </Flex>
            )}
          </Flex>

          <Flex flexDirection={'row'} color={'#A3A3A3'} align="center" gap={1}>
            {type === 'burn' ? (
              <Box width="1.25rem">
                <Image objectFit="cover" src={portfolioImageUrl} />
              </Box>
            ) : (
              hasInsufficientToken && (
                <Box width="1.25rem">
                  <Image objectFit="cover" src={cardanoSrc} />
                </Box>
              )
            )}
            {type === 'mint' && !hasInsufficientToken && (
              <Flex
                justify="right"
                fontSize="0.65rem"
                color="#a3a3a3"
                fontWeight={400}
                display={'flex'}
                flexDirection={'row'}
                align="center"
                flexDir="column"
              >
                {!inputValue.eq(0, 10) && <Box>ADA worth of</Box>}

                <Flex align="center">
                  {portfolioAssets
                    .sort((a, b) =>
                      a.asset.ticker.localeCompare(b.asset.ticker),
                    )
                    .slice(0, 2)
                    .map((assetInPortfolio, i) => (
                      <Box
                        key={assetInPortfolio.asset.id + i}
                        width="2rem"
                        height="2rem"
                        overflow="hidden"
                        borderRadius="50%"
                        ml="-0.5rem"
                        zIndex={3 + i}
                        position="relative"
                      >
                        <Image
                          key={assetInPortfolio.asset.id + i}
                          src={assetInPortfolio.asset.imageUrl}
                          alt={assetInPortfolio.asset.ticker}
                        />
                      </Box>
                    ))}
                  {portfolioAssets.length > 2 && (
                    <Box
                      fontSize="0.725rem"
                      alignItems="center"
                      justifyContent="center"
                      color="white"
                      background={themeVariables.purple.metera}
                      width="28px"
                      height="28px"
                      borderRadius="full"
                      padding="0.3rem"
                      marginLeft="-1rem"
                      zIndex={4}
                    >
                      <Flex
                        align="center"
                        height="full"
                        justify="center"
                        fontWeight={700}
                      >
                        +{portfolioAssets.length - 2}
                      </Flex>
                    </Box>
                  )}
                </Flex>
              </Flex>
            )}
            <Text
              fontSize="1.5rem"
              fontWeight="500"
              lineHeight="32px"
              letterSpacing="-0.02em"
              textAlign="right"
              paddingY={'4px'}
            >
              {type === 'mint' ? '' : portfolioTicker}
              {type === 'mint' && hasInsufficientToken && 'ADA'}
            </Text>
            {/* @todo Connect the dollar exchange API */}
            {/* <Text
            fontSize="12px"
            fontWeight="400"
            lineHeight="20px"
            textAlign="right"
          >
            {'$493.22'}
          </Text> */}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderReceiveInputBox = () => {
    const receiveValue = type === 'mint' ? expectedAmount : withdrawLovelace;
    return (
      <Flex
        border={`1px solid ${themeVariables.cards.stroke}`}
        bg={`${themeVariables.cards.background}, linear-gradient(180deg, rgba(139, 92, 246, 0.03) 0%, rgba(255, 255, 255, 0.00) 100%), rgba(27, 27, 30, 0.90)`}
        _hover={{ border: `1px solid ${themeVariables.purple.metera}` }}
        _focusWithin={{ border: `1px solid ${themeVariables.purple.metera}` }}
        height="105px"
        borderRadius="1rem"
        padding="1.5rem"
        width="full"
        align="center"
      >
        <Flex
          justifyContent={'space-between'}
          alignItems={'center'}
          width={'100%'}
          gap={'8px'}
        >
          <Flex
            position={'relative'}
            width={'full'}
            flex={1}
            alignItems={'flex-start'}
          >
            <Flex flexDir="column" gap={1}>
              <Box color="#A3A3A3">Receive</Box>
              <NumericFormat
                className="mint-input"
                value={
                  receiveValue.gt(0)
                    ? receiveValue
                        .abs()
                        .div(10 ** MTK_DECIMALS)
                        .toString()
                    : ''
                }
                valueIsNumericString
                placeholder={type === 'mint' ? '0.0' : 'ADA'}
                thousandSeparator
                readOnly
                decimalScale={MTK_DECIMALS}
                style={{
                  width: '100%',
                  color: themeVariables.offWhite.offWhite400,
                  fontSize: '32px',
                  fontWeight: '500',
                  lineHeight: '32px',
                  letterSpacing: '-0.02em',
                  textAlign: 'left',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: 0,
                  outline: 'none',
                }}
              />
            </Flex>
          </Flex>
          <Flex
            flexDirection={'column'}
            height="full"
            justify="center"
            color={'#A3A3A3'}
          >
            <Flex
              flexDirection={'row'}
              color={'#A3A3A3'}
              gap={1}
              align="center"
            >
              {type === 'burn' ? (
                <Flex
                  justify="right"
                  fontSize="0.65rem"
                  color="#a3a3a3"
                  fontWeight={400}
                  display={'flex'}
                  flexDirection={'row'}
                  align="center"
                  flexDir="column"
                >
                  {!inputValue.eq(0, 10) && <Box>ADA worth of</Box>}
                  <Flex align="center">
                    {portfolioAssets
                      .sort((a, b) =>
                        a.asset.ticker.localeCompare(b.asset.ticker),
                      )
                      .slice(0, 2)
                      .map((assetInPortfolio, i) => (
                        <Box
                          key={assetInPortfolio.asset.id + i}
                          width="2rem"
                          height="2rem"
                          overflow="hidden"
                          borderRadius="50%"
                          ml="-0.5rem"
                          zIndex={3 + i}
                          position="relative"
                        >
                          <Image
                            key={assetInPortfolio.asset.id + i}
                            src={assetInPortfolio.asset.imageUrl}
                            alt={assetInPortfolio.asset.ticker}
                          />
                        </Box>
                      ))}
                    {portfolioAssets.length > 2 && (
                      <Box
                        fontSize="0.725rem"
                        alignItems="center"
                        justifyContent="center"
                        color="white"
                        background={themeVariables.purple.metera}
                        width="28px"
                        height="28px"
                        borderRadius="full"
                        padding="0.3rem"
                        marginLeft="-1rem"
                        zIndex={4}
                      >
                        <Flex
                          align="center"
                          height="full"
                          justify="center"
                          fontWeight={700}
                        >
                          +{portfolioAssets.length - 2}
                        </Flex>
                      </Box>
                    )}
                  </Flex>
                </Flex>
              ) : (
                <Box width="1.25rem">
                  <Image
                    objectFit="cover"
                    src={type === 'mint' ? portfolioImageUrl : cardanoSrc}
                  />
                </Box>
              )}
              <Text
                fontSize="1.5rem"
                fontWeight="500"
                lineHeight="32px"
                letterSpacing="-0.02em"
                textAlign="right"
                paddingY={'4px'}
              >
                {type === 'mint' ? portfolioTicker : ''}
              </Text>
            </Flex>
            <Flex justify="right">
              {type === 'mint' && (
                <Flex
                  justify="right"
                  fontSize="0.65rem"
                  color="#a3a3a3"
                  fontWeight={400}
                  display={'flex'}
                  flexDirection={'row'}
                  gap={2}
                  align="center"
                >
                  <ProfileSvg color="#A3A3A3" />

                  <span>
                    {BigNumber(balance?.[mtkUnit]?.toString() || 0)
                      .div(10 ** MTK_DECIMALS)
                      .toFixed(2)}
                  </span>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  };

  return (
    <Flex flexDirection={'column'} alignItems={'center'}>
      <Flex
        position="relative"
        width="full"
        alignItems="center"
        flexDirection="column"
        gap="0.3rem"
      >
        {renderSendInputBox()}
        <Flex
          position="absolute"
          background={background ? background : '#08040f'}
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          width="2rem"
          height="2rem"
          borderRadius="50%"
          border={`1px solid ${themeVariables.cards.stroke}`}
          justify="center"
          align="center"
          cursor="pointer"
          _hover={{ border: `1px solid ${themeVariables.purple.metera}` }}
          onClick={(e) => {
            switchType(type === 'mint' ? 'burn' : 'mint');
            setInputValue(new BigNumber(0));
          }}
        >
          <Image src={swapSrc} objectFit="cover" />
        </Flex>
        {renderReceiveInputBox()}
      </Flex>

      <Flex
        alignItems="center"
        width="full"
        gap={'0.5rem'}
        onClick={showBreakdown}
        cursor={'pointer'}
        paddingY="0.5rem"
        height="100%"
        align="center"
      >
        {!inputValue.eq(0, 10) && (
          <Flex flexDir="column" gap="0.5rem" width="full">
            <Box
              height={'auto'}
              width={'auto'}
              padding="0rem"
              border={`1px solid ${themeVariables.cards.stroke}`}
              sx={{
                cursor: 'pointer',
                border: '2px solid transparent',
                transition: 'border 0.2s, transform 0.2s',
                position: 'relative',
                borderRadius: '0.75rem',
              }}
            >
              {' '}
              <Flex
                align="center"
                gap={2}
                border={`1px solid ${themeVariables.cards.stroke}`}
                borderRadius="0.75rem"
                padding="1rem"
                fontSize="0.875rem"
                width="100%"
                justify="center"
                bg="var(--bg-100)"
              >
                {hasInsufficientToken ? (
                  <Box color="#39b75a" fontSize="1rem" fontWeight={500}>
                    Mint MTKs by providing ADA. Click for details.
                  </Box>
                ) : (
                  <>
                    {' '}
                    <StatusDot showStatus={false} status="confirmed" />
                    <Box fontSize="1rem" color="#39b75a">
                      Assets found in wallet. View breakdown.
                    </Box>
                  </>
                )}
              </Flex>
            </Box>
            {type === 'mint' && hasInsufficientToken ? (
              <StepGuide step="1" hasBorder />
            ) : (
              ''
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

export default BuySellInput;
